/**
 * Garmin の睡眠応答（生データ）を表で見せるための整形
 */

import { parseUtcDate } from '@/lib/date-utils'

export type RawRow =
  | { path: string; value: string; /** 時刻の値を読める形にしたもの */ time?: string }
  | { path: string; items: unknown[] }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const formatters = new Map(
  ['Asia/Tokyo', 'UTC'].map((timeZone) => [
    timeZone,
    new Intl.DateTimeFormat('ja-JP', {
      timeZone,
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }),
  ]),
)

const formatDate = (date: Date, timeZone: string): string | undefined => {
  if (Number.isNaN(date.getTime())) return undefined
  const parts = formatters.get(timeZone)?.formatToParts(date) ?? []
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('month')}/${get('day')}(${get('weekday')}) ${get('hour')}:${get('minute')}`
}

/**
 * キー名が GMT で終わる値は日本時間、Local で終わる値は現地の時刻にする。
 * Garmin の Local は現地の時刻を UTC として表した値なので、UTC のまま読むと現地の時刻になる
 */
const formatRawTime = (key: string, value: unknown): string | undefined => {
  const timeZone = /gmt$/i.test(key) ? 'Asia/Tokyo' : /local$/i.test(key) ? 'UTC' : null
  if (timeZone === null) return undefined
  if (typeof value === 'number') return formatDate(new Date(value), timeZone)
  if (typeof value === 'string') return formatDate(parseUtcDate(value), timeZone)
  return undefined
}

/** 入れ子のオブジェクトはキーをドットでつないで 1 行ずつにする。配列は件数と中身をまとめて 1 行にする */
export const flattenRaw = (value: Record<string, unknown>, prefix = ''): RawRow[] =>
  Object.entries(value).flatMap(([key, v]): RawRow[] => {
    const path = prefix ? `${prefix}.${key}` : key
    if (Array.isArray(v)) return [{ path, items: v }]
    if (isRecord(v)) return Object.keys(v).length > 0 ? flattenRaw(v, path) : [{ path, value: '{}' }]
    const time = formatRawTime(key, v)
    return [{ path, value: v === null || v === undefined ? 'null' : String(v), ...(time && { time }) }]
  })
